import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    Plus,
    Building2,
    Check,
    ChevronDown,
    ChevronRight,
    X,
    Settings2,
    Tag,
    Lock,
    Users,
} from "lucide-react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Label } from "reactstrap";
import BackButton from "../../../components/custom/BackButton";
import { createdUpdatedInfo, notify } from "../../../utils";
import ActionIcon from "../../../components/custom/ActionIcon";
import useDepartments from "./useDepartments";
import useItemTypes from "./useItemTypes";
import useAttributeDefinitions from "./useAttributeDefinitions";
import useDepartmentAccess from "./useDepartmentAccess";
import { getErrorMessage } from "./errorMessage";
import { AttributeDataType, AttributeDefinition, Department, ItemType } from "./configTypes";
import useEmployees from "./useEmployees";
import { ChoiceEmployee } from "../../../globals/interfaces";
import VirtualTable from "../../../components/custom/VirtualTable";
import useUser from "../../../customHooks/useUser";

/* ------------------------------------------------------------------ */
/*  Addable-users pool for department access — a stand-in for the      */
/*  real employees source until this is swapped to /team-member.       */
/* ------------------------------------------------------------------ */

interface Employee {
    id: string;
    name: string;
    email?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const camelKey = (label: string): string => {
    const parts = String(label).trim().toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (parts.length === 0) return "attr" + Date.now();
    return parts[0] + parts.slice(1).map((p) => p[0].toUpperCase() + p.slice(1)).join("");
};

/* ------------------------------------------------------------------ */
/*  Design tokens + responsive helpers                                 */
/* ------------------------------------------------------------------ */

const c = {
    slate50: "#f8fafc",
    slate100: "#f1f5f9",
    slate200: "#e2e8f0",
    slate300: "#cbd5e1",
    slate400: "#94a3b8",
    slate500: "#64748b",
    slate600: "#475569",
    slate700: "#334155",
    slate800: "#1e293b",
    slate900: "#0f172a",
    amber50: "#5dc904",
    amber100: "#6fb327",
    amber300: "#6fb327",
    amber400: "#3db264",
    amber500: "#9ecd7e",
    amber700: "#010407",
    amber900: "#f8f7f6",
    red50: "#fef2f2",
    red100: "#fee2e2",
    red300: "#fca5a5",
    red500: "#ef4444",
    red600: "#dc2626",
    white: "#ffffff",
};

const media = {
    sm: "@media (min-width: 640px)",
    md: "@media (min-width: 768px)",
    lg: "@media (min-width: 1024px)",
};

/* ------------------------------------------------------------------ */
/*  Styled primitives                                                  */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  margin: 0 auto;
  padding: 24px 16px 64px;
  ${media.sm} {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const Header = styled.header`
  margin-bottom: 24px;
`;
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
`;

const TabsNav = styled.nav`
  margin-top: 20px;
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${c.slate200};
`;
const Tab = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  margin-bottom: -1px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  border-bottom: 2px solid ${(p) => (p.active ? c.amber400 : "transparent")};
  color: ${(p) => (p.active ? c.slate900 : c.slate500)};
  transition: color 0.15s;
  &:hover {
    color: ${(p) => (p.active ? c.slate900 : c.slate800)};
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${c.slate500};
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${c.slate900};
  background: ${c.white};
  border: 1px solid ${(p) => (p.error ? c.red300 : c.slate200)};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &::placeholder {
    color: ${c.slate400};
  }
  &:focus {
    border-color: ${(p) => (p.error ? c.red300 : c.amber400)};
    box-shadow: 0 0 0 2px ${(p) => (p.error ? c.red100 : c.amber100)};
  }
`;

const PrimaryButton = styled.button<{ sm?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: ${c.amber400};
  color: ${c.slate900};
  font-size: 14px;
  font-weight: 500;
  padding: ${(p) => (p.sm ? "6px 12px" : "8px 16px")};
  transition: background 0.15s;
  &:hover {
    background: ${c.amber500};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const GhostButton = styled.button<{ sm?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid ${c.slate200};
  border-radius: 8px;
  cursor: pointer;
  background: ${c.white};
  color: ${c.slate700};
  font-size: 14px;
  font-weight: 500;
  padding: ${(p) => (p.sm ? "6px 10px" : "8px 12px")};
  transition: background 0.15s;
  &:hover {
    background: ${c.slate50};
  }
`;

const CountSpan = styled.span`
  margin-left: auto;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${c.slate400};
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${c.red600};
`;
const Block = styled.div<{ mt?: number }>`
  margin-top: ${(p) => p.mt || 0}px;
`;

const CheckboxInput = styled.input`
  height: 16px;
  width: 16px;
  accent-color: ${c.amber500};
`;

/* Item types */
const TypesWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const DeptTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${c.slate700};
`;
const TypesGrid = styled.div`
  display: grid;
  align-items: start;
  gap: 16px;
  grid-template-columns: 1fr;
  ${media.md} {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const AddTypeButton = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${c.slate900};
  &:hover {
    text-decoration: underline;
  }
`;
const NewTypeCard = styled.div`
  border: 1px dashed ${c.slate300};
  border-radius: 12px;
  background: ${c.white};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const NewTypeActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;
const EmptyDept = styled.p`
  font-size: 14px;
  color: ${c.slate500};
  margin: 0;
`;
const TypeCard = styled.div`
  border: 1px solid ${c.slate200};
  border-radius: 12px;
  background: ${c.white};
`;
const TypeCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid ${c.slate100};
  padding: 12px 16px;
`;
const TypeCardHeaderMain = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
`;
const TypeName = styled.h3`
  font-weight: 500;
  margin: 0;
  font-size: 16px;
`;
const DefRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 16px;
  font-size: 14px;
  &:not(:first-child) {
    border-top: 1px solid ${c.slate100};
  }
`;
const DefMain = styled.div`
  min-width: 0;
  flex: 1;
`;
const DefLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const DefLabelText = styled.span`
  font-weight: 500;
  color: ${c.slate800};
`;
const ReqBadge = styled.span`
  border-radius: 4px;
  background: ${c.red50};
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${c.red600};
`;
const FilterBadge = styled.span`
  border-radius: 4px;
  background: ${c.amber50};
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${c.amber700};
`;
const DefOptions = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: ${c.slate400};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const TypeTag = styled.span`
  border-radius: 4px;
  background: ${c.slate100};
  padding: 2px 6px;
  font-size: 11px;
  color: ${c.slate500};
  white-space: nowrap;
`;
const CardFooter = styled.div`
  border-top: 1px solid ${c.slate100};
  padding: 12px 16px;
`;
const AddAttrLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${c.amber700};
  &:hover {
    text-decoration: underline;
  }
`;
const EmptyDef = styled.p`
  padding: 16px;
  font-size: 14px;
  color: ${c.slate500};
  margin: 0;
`;
const NewAttrBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 8px;
  background: ${c.slate50};
  padding: 12px;
`;
const CheckRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
`;
const SmallCheckLabel = styled.label`
  display: flex;
  cursor: pointer;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${c.slate600};
`;
const NewAttrActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const Select = styled.select`
  width: 100%;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${c.slate900};
  background: ${c.white};
  border: 1px solid ${c.slate200};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: ${c.amber400};
    box-shadow: 0 0 0 2px ${c.amber100};
  }
  &:disabled {
    background: ${c.slate50};
    color: ${c.slate400};
    cursor: not-allowed;
  }
`;

/* ------------------------------------------------------------------ */
/*  Root — dev/admin area only. Enduser Inventory/Add item/Assign      */
/*  lives at src/views/managers/Inventory/ (see inventory-frontend-    */
/*  spec.md Step 0). Departments/Item Types/Attribute Definitions are  */
/*  wired to the real config API per inventory-dev-routes.md.          */
/* ------------------------------------------------------------------ */

type DevTab = "departments" | "types";

export default function InventoryDemo() {
    const { departments, createDepartment, deleteDepartment } = useDepartments();
    const { itemTypes, createItemType, deleteItemType } = useItemTypes();
    const { definitions, createAttributeDefinition, deleteAttributeDefinition } = useAttributeDefinitions();
    const { getAccess, setAccess } = useDepartmentAccess();
    const { employees } = useEmployees();

    const [tab, setTab] = useState<DevTab>("departments");

    const defsForType = (typeId: number) => definitions.filter((d) => d.itemTypeId === typeId);

    const [accessDept, setAccessDept] = useState<Department | null>(null);

    return (
        <div>
            <Container>
                <Header>
                    <HeaderRow>
                        <Title>Inventory — Dev config</Title>
                    </HeaderRow>

                    <TabsNav>
                        <Tab active={tab === "departments"} onClick={() => setTab("departments")}>
                            <Building2 size={16} />
                            Departments
                        </Tab>
                        <Tab active={tab === "types"} onClick={() => setTab("types")}>
                            <Settings2 size={16} />
                            Item types
                        </Tab>
                    </TabsNav>
                </Header>

                {tab === "departments" && (
                    <DepartmentsView
                        departments={departments}
                        itemTypes={itemTypes}
                        onCreateDepartment={async (name, code) => {
                            try {
                                await createDepartment(name, code);
                                notify("Department created");
                            } catch (err) {
                                notify(getErrorMessage(err, "Couldn't create that department."), "error");
                            }
                        }}
                        onDeleteDepartment={async (dept) => {
                            try {
                                await deleteDepartment(dept.id);
                                notify("Department deleted");
                            } catch (err) {
                                notify(getErrorMessage(err, "Couldn't delete that department."), "error");
                            }
                        }}
                        onManageAccess={(dept) => setAccessDept(dept)}
                    />
                )}

                {tab === "types" && (
                    <ItemTypesView
                        departments={departments}
                        itemTypes={itemTypes}
                        defsForType={defsForType}
                        onAddDefinition={async (payload) => {
                            try {
                                await createAttributeDefinition(payload);
                                notify("Attribute added to type");
                            } catch (err) {
                                notify(getErrorMessage(err, "Couldn't add that attribute."), "error");
                            }
                        }}
                        onDeleteDefinition={async (def) => {
                            try {
                                await deleteAttributeDefinition(def.id);
                                notify("Attribute deleted");
                            } catch (err) {
                                notify(getErrorMessage(err, "Couldn't delete that attribute."), "error");
                            }
                        }}
                        onAddType={async (departmentId, name, code) => {
                            try {
                                await createItemType(departmentId, name, code);
                                notify("Item type created");
                            } catch (err) {
                                notify(getErrorMessage(err, "Couldn't create that item type."), "error");
                            }
                        }}
                        onDeleteType={async (type) => {
                            try {
                                await deleteItemType(type.id);
                                notify("Item type deleted");
                            } catch (err) {
                                notify(getErrorMessage(err, "Couldn't delete that item type."), "error");
                            }
                        }}
                    />
                )}
            </Container>

            <DepartmentAccessModal
                modal={!!accessDept}
                setModal={(open) => !open && setAccessDept(null)}
                department={accessDept}
                users={employees}
                getAccess={getAccess}
                onSave={async (departmentId, userIds) => {
                    try {
                        await setAccess(departmentId, userIds);
                        notify("Access updated");
                        setAccessDept(null);
                    } catch (err) {
                        notify(getErrorMessage(err, "Couldn't update access."), "error");
                    }
                }}
            />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Item types — the registry that drives forms & filters              */
/* ------------------------------------------------------------------ */

interface ItemTypesViewProps {
    departments: Department[];
    itemTypes: ItemType[];
    defsForType: (typeId: number) => AttributeDefinition[];
    onAddDefinition: (payload: {
        itemTypeId: number;
        label: string;
        dataType: AttributeDataType;
        attributeKey?: string;
        isRequired?: boolean;
        isFilterable?: boolean;
        options?: string[];
    }) => void;
    onDeleteDefinition: (def: AttributeDefinition) => void;
    onAddType: (departmentId: number, name: string, code?: string) => void;
    onDeleteType: (type: ItemType) => void;
}

function ItemTypesView({ departments, itemTypes, defsForType, onAddDefinition, onDeleteDefinition, onAddType, onDeleteType }: ItemTypesViewProps) {
    return (
        <TypesWrap>
            {departments.map((dept) => (
                <DepartmentSection
                    key={dept.id}
                    dept={dept}
                    types={itemTypes.filter((t) => t.departmentId === dept.id)}
                    defsForType={defsForType}
                    onAddDefinition={onAddDefinition}
                    onDeleteDefinition={onDeleteDefinition}
                    onAddType={onAddType}
                    onDeleteType={onDeleteType}
                />
            ))}
        </TypesWrap>
    );
}

interface DepartmentSectionProps {
    dept: Department;
    types: ItemType[];
    defsForType: (typeId: number) => AttributeDefinition[];
    onAddDefinition: ItemTypesViewProps["onAddDefinition"];
    onDeleteDefinition: ItemTypesViewProps["onDeleteDefinition"];
    onAddType: ItemTypesViewProps["onAddType"];
    onDeleteType: ItemTypesViewProps["onDeleteType"];
}

function DepartmentSection({ dept, types, defsForType, onAddDefinition, onDeleteDefinition, onAddType, onDeleteType }: DepartmentSectionProps) {
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [error, setError] = useState("");

    const save = () => {
        if (!name.trim()) {
            setError("Enter a type name.");
            return;
        }
        onAddType(dept.id, name.trim(), code.trim() || undefined);
        setName("");
        setCode("");
        setError("");
        setAdding(false);
    };

    const cancel = () => {
        setName("");
        setCode("");
        setError("");
        setAdding(false);
    };

    return (
        <div>
            <DeptTitle>
                <Building2 size={16} color={c.slate400} />
                {dept.name}
                {!adding && (
                    <ActionIcon
                        type={'add'}
                        onClick={() => setAdding(true)}
                    />
                )}
            </DeptTitle>
            <TypesGrid>
                {types.map((t) => (
                    <ItemTypeCard
                        key={t.id}
                        type={t}
                        defs={defsForType(t.id)}
                        onAddDefinition={onAddDefinition}
                        onDeleteDefinition={onDeleteDefinition}
                        onDeleteType={onDeleteType}
                    />
                ))}
                {adding && (
                    <NewTypeCard>
                        <div>
                            <FieldLabel>New item type in {dept.name}</FieldLabel>
                            <Input
                                autoFocus
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && save()}
                                placeholder="e.g. Docking station"
                                error={!!error}
                            />
                            {error && <ErrorText>{error}</ErrorText>}
                        </div>
                        <div>
                            <FieldLabel>Code (optional)</FieldLabel>
                            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. LAP" />
                        </div>
                        <NewTypeActions>
                            <GhostButton sm onClick={cancel}>
                                <X size={16} />
                                Cancel
                            </GhostButton>
                            <PrimaryButton sm onClick={save}>
                                <Check size={16} />
                                Create type
                            </PrimaryButton>
                        </NewTypeActions>
                    </NewTypeCard>
                )}
            </TypesGrid>
            {types.length === 0 && !adding && (
                <EmptyDept>None</EmptyDept>
            )}
        </div>
    );
}

interface ItemTypeCardProps {
    type: ItemType;
    defs: AttributeDefinition[];
    onAddDefinition: ItemTypesViewProps["onAddDefinition"];
    onDeleteDefinition: ItemTypesViewProps["onDeleteDefinition"];
    onDeleteType: ItemTypesViewProps["onDeleteType"];
}

function ItemTypeCard({ type, defs, onAddDefinition, onDeleteDefinition, onDeleteType }: ItemTypeCardProps) {
    const [adding, setAdding] = useState(false);
    const [collapsed, setCollapsed] = useState(true);

    return (
        <TypeCard>
            <TypeCardHeader>
                <TypeCardHeaderMain onClick={() => setCollapsed((v) => !v)}>
                    {collapsed ? <ChevronRight size={16} color={c.slate400} /> : <ChevronDown size={16} color={c.slate400} />}
                    <Tag size={16} color={c.amber500} />
                    <TypeName>{type.name}</TypeName>
                    <CountSpan>{defs.length} attrs</CountSpan>
                </TypeCardHeaderMain>
                <ActionIcon
                    type="delete"
                    confirm
                    confirmMessage={`Delete item type "${type.name}"?`}
                    onClick={() => onDeleteType(type)}
                />
            </TypeCardHeader>

            {!collapsed && (
                <>
                    <div>
                        {defs.length === 0 && <EmptyDef>No attributes yet.</EmptyDef>}
                        {defs.map((d) => (
                            <DefRow key={d.id}>
                                <DefMain>
                                    <DefLabelRow>
                                        <DefLabelText>{d.label}</DefLabelText>
                                        {d.isRequired && <ReqBadge>Required</ReqBadge>}
                                        {d.isFilterable && <FilterBadge>Filter</FilterBadge>}
                                    </DefLabelRow>
                                    {d.options.length > 0 && <DefOptions>{d.options.join(" · ")}</DefOptions>}
                                </DefMain>
                                <TypeTag>{d.dataType}</TypeTag>
                                <ActionIcon
                                    type="delete"
                                    confirm
                                    confirmMessage={`Delete attribute "${d.label}"?`}
                                    onClick={() => onDeleteDefinition(d)}
                                />
                            </DefRow>
                        ))}
                    </div>

                    <CardFooter>
                        {adding ? (
                            <NewAttributeForm
                                typeId={type.id}
                                onCancel={() => setAdding(false)}
                                onSave={(def) => {
                                    onAddDefinition(def);
                                    setAdding(false);
                                }}
                            />
                        ) : (
                            <AddAttrLink onClick={() => setAdding(true)}>
                                <Plus size={16} />
                                Add attribute
                            </AddAttrLink>
                        )}
                    </CardFooter>
                </>
            )}
        </TypeCard>
    );
}

interface NewAttributeFormProps {
    typeId: number;
    onCancel: () => void;
    onSave: ItemTypesViewProps["onAddDefinition"];
}

function NewAttributeForm({ typeId, onCancel, onSave }: NewAttributeFormProps) {
    const [label, setLabel] = useState("");
    const [dataType, setDataType] = useState<AttributeDataType>("list");
    const [isRequired, setIsRequired] = useState(false);
    const [isFilterable, setIsFilterable] = useState(true);
    const [optionsText, setOptionsText] = useState("");
    const [err, setErr] = useState("");

    const save = () => {
        if (!label.trim()) {
            setErr("Enter a label.");
            return;
        }
        const options = dataType === "list" ? optionsText.split(",").map((s) => s.trim()).filter(Boolean) : [];
        if (dataType === "list" && options.length === 0) {
            setErr("list attributes need at least one option.");
            return;
        }
        onSave({
            itemTypeId: typeId,
            attributeKey: camelKey(label),
            label: label.trim(),
            dataType,
            isRequired,
            isFilterable: dataType === "string" ? false : isFilterable,
            options,
        });
    };

    return (
        <NewAttrBox>
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}>
                <div>
                    <FieldLabel>Label</FieldLabel>
                    <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Warranty" />
                </div>
                <div>
                    <FieldLabel>Type</FieldLabel>
                    <Select value={dataType} onChange={(e) => setDataType(e.target.value as AttributeDataType)}>
                        <option value="list">List (pick option)</option>
                        <option value="string">Text</option>
                        <option value="number">Number</option>
                        <option value="bool">Yes / No</option>
                        <option value="date">Date</option>
                    </Select>
                </div>
            </div>

            {dataType === "list" && (
                <div>
                    <FieldLabel>Options (comma separated)</FieldLabel>
                    <Input
                        value={optionsText}
                        onChange={(e) => setOptionsText(e.target.value)}
                        placeholder="1 year, 2 years, 3 years"
                    />
                </div>
            )}

            <CheckRow>
                <SmallCheckLabel>
                    <CheckboxInput type="checkbox" checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />
                    Required
                </SmallCheckLabel>
                {dataType !== "string" && (
                    <SmallCheckLabel>
                        <CheckboxInput type="checkbox" checked={isFilterable} onChange={(e) => setIsFilterable(e.target.checked)} />
                        Filterable
                    </SmallCheckLabel>
                )}
            </CheckRow>

            {err && <ErrorText>{err}</ErrorText>}

            <NewAttrActions>
                <GhostButton sm onClick={onCancel}>
                    <X size={16} />
                    Cancel
                </GhostButton>
                <PrimaryButton sm onClick={save}>
                    <Check size={16} />
                    Save attribute
                </PrimaryButton>
            </NewAttrActions>
        </NewAttrBox>
    );
}

/* ------------------------------------------------------------------ */
/*  Departments (dev) — create departments + manage per-dept access     */
/* ------------------------------------------------------------------ */

interface DepartmentsViewProps {
    departments: Department[];
    itemTypes: ItemType[];
    onCreateDepartment: (name: string, code?: string) => void;
    onDeleteDepartment: (dept: Department) => void;
    onManageAccess: (dept: Department) => void;
}

function DepartmentsView({ departments, itemTypes, onCreateDepartment, onDeleteDepartment, onManageAccess }: DepartmentsViewProps) {
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [error, setError] = useState("");

    const create = () => {
        if (!name.trim()) {
            setError("Enter a department name.");
            return;
        }
        onCreateDepartment(name.trim(), code.trim() || undefined);
        setName("");
        setCode("");
        setError("");
        setAdding(false);
    };

    const cancel = () => {
        setName("");
        setCode("");
        setError("");
        setAdding(false);
    };

    return (
        <div>
            <SectionHeaderRow>
                <SectionTitle>Departments</SectionTitle>
                {!adding && (
                    <ActionIcon
                        type={'add'}
                        onClick={() => setAdding(true)}
                    />
                )}
            </SectionHeaderRow>

            {adding && (
                <NewTypeCard style={{ marginBottom: 16 }}>
                    <div>
                        <FieldLabel>New department</FieldLabel>
                        <Input
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && create()}
                            placeholder="e.g. Maintenance"
                            error={!!error}
                        />
                        {error && <ErrorText>{error}</ErrorText>}
                    </div>
                    <div>
                        <FieldLabel>Code (optional)</FieldLabel>
                        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. MX" />
                    </div>
                    <NewTypeActions>
                        <GhostButton sm onClick={cancel}>
                            <X size={16} />
                            Cancel
                        </GhostButton>
                        <PrimaryButton sm onClick={create}>
                            <Check size={16} />
                            Create department
                        </PrimaryButton>
                    </NewTypeActions>
                </NewTypeCard>
            )}

            <DeptGrid>
                {departments.map((d) => {
                    const typeCount = itemTypes.filter((t) => t.departmentId === d.id).length;
                    return (
                        <DeptManageCard key={d.id}>
                            <DeptManageHead>
                                <Building2 size={16} color={c.amber500} />
                                <TypeName>{d.name}</TypeName>
                                <ActionIcon
                                    type="delete"
                                    confirm
                                    confirmMessage={`Delete department "${d.name}"?`}
                                    onClick={() => onDeleteDepartment(d)}
                                />
                            </DeptManageHead>
                            <DeptMeta>
                                <span>
                                    {typeCount} item {typeCount === 1 ? "type" : "types"}
                                </span>
                            </DeptMeta>
                            <DeptManageFooter>
                                <GhostButton sm onClick={() => onManageAccess(d)}>
                                    <Users size={16} />
                                    Assign access
                                </GhostButton>
                            </DeptManageFooter>
                        </DeptManageCard>
                    );
                })}
            </DeptGrid>
        </div>
    );
}

interface DepartmentAccessModalProps {
    modal: boolean;
    setModal: (open: boolean) => void;
    department: Department | null;
    users: ChoiceEmployee[];
    getAccess: (departmentId: number) => Promise<{ departmentId: number; userIds: string[] }>;
    onSave: (departmentId: number, userIds: string[]) => void;
}

function DepartmentAccessModal({ modal, setModal, department, users, getAccess, onSave }: DepartmentAccessModalProps) {
    const [selected, setSelected] = useState<string[]>([]);
    const [query, setQuery] = useState("");

    const toggle = () => setModal(!modal);

    // Fetch the current access list when opened; stage locally, commit on save.
    useEffect(() => {
        if (modal && department) {
            setQuery("");
            getAccess(department.id).then((res) => setSelected(res.userIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal, department?.id]);

    if (!department) return null;

    const assigned = users.filter((u) => selected.includes(String(u.id)));
    const q = query.trim().toLowerCase();
    const available = users.filter(
        (u) =>
            !selected.includes(String(u.id)) &&
            (!q || (`${u.s_first_name} ${u.s_last_name}`).toLowerCase().includes(q) || (u.s_email || "").toLowerCase().includes(q))
    );

    const add = (id: string) => setSelected((prev) => [...prev, id]);
    const remove = (id: string) => setSelected((prev) => prev.filter((x) => x !== id));

    return (
        <CustomModal isOpen={modal} toggle={toggle} size={'lg'}>
            <ModalHeader className={"d-flex"}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={"pl-2"}>Manage access — {department.name}</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <AccessSectionTitle>Assigned ({assigned.length})</AccessSectionTitle>
                {assigned.length === 0 ? (
                    <AccessEmpty>No users assigned yet.</AccessEmpty>
                ) : (
                    <UserList>
                        {assigned.map((u) => (
                            <UserRow key={u.id}>
                                <UserMeta>
                                    <UserName>{u.s_first_name} {u.s_last_name} {u.s_email && <UserEmail>{u.s_email}</UserEmail>}</UserName>
                                </UserMeta>
                                <RowRemoveBtn onClick={() => remove(String(u.id))} title="Remove access">
                                    <X size={16} />
                                </RowRemoveBtn>
                            </UserRow>
                        ))}
                    </UserList>
                )}

                <Block mt={16}>
                    <AccessSectionTitle>Add users</AccessSectionTitle>
                    <VirtualTable<ChoiceEmployee>
                        data={available}
                        mapping={[{
                            name: 'Email',
                            value: 's_email'
                        }, {
                            name: 'First name',
                            value: 's_first_name',
                            customWidth: 125
                        }, {
                            name: 'Last name',
                            value: 's_last_name',
                            customWidth: 150
                        }, {
                            name: 'Unit',
                            value: 's_home_station',
                            customWidth: 75
                        }, {
                            name: 'Job title',
                            value: 's_job_title'
                        }, {
                            name: 'Department',
                            value: 's_department',
                            customWidth: 150
                        }, {
                            name: '',
                            value: 'fas fa-plus',
                            icon: true,
                            function: item => add(String(item.id))
                        }]}
                        numRows={10}
                    />
                </Block>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>{createdUpdatedInfo({})}</Label>
                    <FooterButtonsContainer>
                        <ActionIcon type={"save"} onClick={() => onSave(department.id, selected)} />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </CustomModal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;
const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;
const FooterContentContainer = styled.div`
    width: 100%;
`;
const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${(p) => (p.update ? "space-between" : "flex-end")};
`;

const DevBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
    padding: 10px 12px;
    border: 1px solid ${c.slate200};
    border-radius: 8px;
    background: ${c.slate50};
    font-size: 13px;
    color: ${c.slate600};
`;
const SectionHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;
const SectionTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: ${c.slate800};
`;
const DeptGrid = styled.div`
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    ${media.sm} {
        grid-template-columns: repeat(2, 1fr);
    }
    ${media.lg} {
        grid-template-columns: repeat(3, 1fr);
    }
`;
const DeptManageCard = styled.div`
    border: 1px solid ${c.slate200};
    border-radius: 12px;
    background: ${c.white};
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;
const DeptManageHead = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;
const DeptMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${c.slate500};
`;
const DeptManageFooter = styled.div`
    margin-top: 4px;
`;
const AccessSectionTitle = styled.p`
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${c.slate500};
`;
const AccessEmpty = styled.p`
    margin: 0;
    padding: 10px 12px;
    font-size: 13px;
    color: ${c.slate500};
    background: ${c.slate50};
    border-radius: 8px;
`;
const UserList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;
const UserRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid ${c.slate200};
    border-radius: 8px;
`;
const UserMeta = styled.div`
    min-width: 0;
`;
const UserName = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${c.slate800};
`;
const UserEmail = styled.span`
    font-size: 12px;
    color: ${c.slate500};
`;
const RowAddBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    border-radius: 6px;
    padding: 4px;
    background: ${c.amber100};
    color: ${c.amber900};
    &:hover {
        filter: brightness(0.97);
    }
`;
const RowRemoveBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    border-radius: 6px;
    padding: 4px;
    background: ${c.slate100};
    color: ${c.slate500};
    &:hover {
        background: ${c.red50};
        color: ${c.red600};
    }
`;

const CustomModal = styled(Modal)`
    max-width: 1200px;
`;